<template>
  <div class="attendance-audit-staff-bind-container">
    <el-card>
      <div slot="header" class="flex justify-between">
        <span>{{ $t('attendanceAuditStaffBind.title') }}</span>
        <div>
          <el-button size="small" @click="handleGoBack">
            <i class="el-icon-close"></i>{{ $t('attendanceAuditStaffBind.back') }}
          </el-button>
        </div>
      </div>

      <el-row :gutter="20">
        <el-col :span="24">
          <el-form label-width="100px">
            <el-form-item :label="$t('attendanceAuditStaffBind.currentStaffLabel')">
              <el-input
                v-model="currentStaffName"
                :placeholder="$t('attendanceAuditStaffBind.currentStaffLabel')"
                clearable
                readonly
                style="width: 240px;"
                @clear="handleClearCurrentStaff"
              >
                <el-button slot="append" icon="el-icon-search" @click="handlePickCurrentStaff"></el-button>
              </el-input>
              <span style="margin-left: 10px; color: #909399; font-size: 12px;">
                {{ $t('attendanceAuditStaffBind.currentStaffTip') }}
              </span>
            </el-form-item>
            <el-form-item :label="$t('attendanceAuditStaffBind.businessTypeLabel')">
              <el-radio-group v-model="searchForm.businessType" @change="handleBusinessTypeChange">
                <el-radio-button label="1001">{{ $t('attendanceAuditStaffBind.leaveOption') }}</el-radio-button>
                <el-radio-button label="1002">{{ $t('attendanceAuditStaffBind.overtimeOption') }}</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item :label="$t('attendanceAuditStaffBind.auditStaffLabel')">
              <el-input
                v-model="searchForm.auditUserNameLike"
                clearable
                style="width: 240px;"
                :placeholder="$t('attendanceAuditStaffBind.auditStaffLabel')"
              />
              <el-button type="primary" style="margin-left: 10px;" @click="handleSearch">
                <i class="el-icon-search"></i>{{ $t('attendanceAuditStaffBind.search') }}
              </el-button>
              <el-button @click="handleReset">
                <i class="el-icon-refresh"></i>{{ $t('attendanceAuditStaffBind.reset') }}
              </el-button>
              <el-button type="primary" style="margin-left: 10px;" @click="handleAdd" :disabled="!searchForm.staffId">
                <i class="el-icon-plus"></i>{{ $t('attendanceAuditStaffBind.add') }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-col>
      </el-row>

      <el-table v-loading="loading" :data="tableData" border style="width: 100%">
        <el-table-column prop="auditUserName" :label="$t('attendanceAuditStaffBind.table.auditUserName')" align="center" />
        <el-table-column prop="businessTypeName" :label="$t('attendanceAuditStaffBind.table.businessType')" align="center" width="120" />
        <el-table-column prop="remark" :label="$t('attendanceAuditStaffBind.table.remark')" align="center" />
        <el-table-column prop="stateName" :label="$t('attendanceAuditStaffBind.table.state')" align="center" width="100" />
        <el-table-column prop="createTime" :label="$t('attendanceAuditStaffBind.table.createTime')" align="center" width="180" />
        <el-table-column :label="$t('common.operation')" align="center" width="150">
          <template slot-scope="scope">
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">
              {{ $t('attendanceAuditStaffBind.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page.sync="page.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="page.size"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        style="margin-top: 20px;"
      />
    </el-card>

    <select-staff ref="selectStaff" @selectStaff="handleSelectStaff"></select-staff>
  </div>
</template>

<script>
import {
  listAttendanceAuditStaffs,
  saveAttendanceAuditStaff,
  deleteAttendanceAuditStaff
} from '@/api/attendance/attendanceAuditStaffApi'
import SelectStaff from '@/components/staff/SelectStaff'

export default {
  name: 'AttendanceAuditStaffBindList',
  components: {
    SelectStaff
  },
  data() {
    return {
      loading: false,
      currentStaffName: '',
      searchForm: {
        businessType: '1001',
        staffId: '',
        staffName: '',
        classesId: '',
        auditUserNameLike: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    // 从路由读取被审核员工和班组
    const { classesId, staffId, staffName } = this.$route.query || {}
    if (classesId) {
      this.searchForm.classesId = classesId
    }
    if (staffId) {
      this.searchForm.staffId = staffId
    }
    if (staffName) {
      this.searchForm.staffName = staffName
      this.currentStaffName = staffName
    }
    this.fetchData()
  },
  methods: {
    async fetchData() {
      // 没选被审核员工就不查
      if (!this.searchForm.staffId) {
        this.tableData = []
        this.page.total = 0
        return
      }
      this.loading = true
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          businessType: this.searchForm.businessType,
          staffId: this.searchForm.staffId,
          staffNameLike: this.searchForm.staffNameLike,
          classesId: this.searchForm.classesId,
          auditUserNameLike: this.searchForm.auditUserNameLike
        }
        const { data, total } = await listAttendanceAuditStaffs(params)
        // 1) 防御性去重 + 过滤脏字典
        // 后端 mapper 用 `t_dict` left join 翻译 businessTypeName，
        // 但因为没限定 table_name 且 t_dict 里有同名 status_cd 的脏记录（如 1001=门禁），
        // 会导致同一条 attendance_audit_staff 被翻倍成业务类型互不相干的若干行。
        // 我们只认为以下业务类型名称是合法的考勤业务：
        const validBusinessTypeNames = ['请假', '加班']
        const rowsAfterTypeFilter = (data || []).filter(row => {
          if (!row) return false
          return validBusinessTypeNames.includes(row.businessTypeName)
        })
        // 2) 按 id 去重，防御后端 join 笛卡尔积
        const seen = new Set()
        const dedup = []
        for (const row of rowsAfterTypeFilter) {
          if (!row.id || seen.has(row.id)) continue
          seen.add(row.id)
          dedup.push(row)
        }
        this.tableData = dedup
        // 3) total 以去重 + 过滤后的真实条数为准，避免分页错位
        this.page.total = dedup.length > 0 ? dedup.length : (total || 0)
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('attendanceAuditStaffBind.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleBusinessTypeChange() {
      this.page.current = 1
      this.fetchData()
    },
    handleSearch() {
      this.page.current = 1
      this.fetchData()
    },
    handleReset() {
      this.searchForm.auditUserNameLike = ''
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.fetchData()
    },
    handleClearCurrentStaff() {
      this.searchForm.staffId = ''
      this.searchForm.staffName = ''
      this.currentStaffName = ''
      this.tableData = []
      this.page.total = 0
    },
    handlePickCurrentStaff() {
      // 复用同一个 SelectStaff 组件：选完后当成"被审核员工"
      this.$refs.selectStaff.open()
      this._selectMode = 'currentStaff'
    },
    handleAdd() {
      if (!this.searchForm.staffId) {
        this.$message.warning(this.$t('attendanceAuditStaffBind.selectCurrentStaffFirst'))
        return
      }
      this._selectMode = 'auditStaff'
      this.$refs.selectStaff.open()
    },
    async handleSelectStaff(staff) {
      if (!staff || !staff.staffId) {
        this.$message.warning(this.$t('attendanceAuditStaffBind.selectStaffFirst'))
        return
      }
      // 选择"当前被审核员工"
      if (this._selectMode === 'currentStaff') {
        this.searchForm.staffId = staff.staffId
        this.searchForm.staffName = staff.staffName
        this.currentStaffName = staff.staffName
        this.page.current = 1
        this.fetchData()
        return
      }
      // 选择"审核人"
      try {
        const res = await saveAttendanceAuditStaff({
          businessType: this.searchForm.businessType,
          staffId: this.searchForm.staffId,
          staffName: this.searchForm.staffName,
          auditUserId: staff.staffId,
          auditUserName: staff.staffName,
          classesId: this.searchForm.classesId
        })
        if (res && res.code === 0) {
          this.$message.success(this.$t('attendanceAuditStaffBind.saveSuccess'))
          this.fetchData()
        } else {
          this.$message.error((res && res.msg) || this.$t('attendanceAuditStaffBind.saveFailed'))
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('attendanceAuditStaffBind.saveFailed'))
      }
    },
    handleDelete(row) {
      this.$confirm(this.$t('attendanceAuditStaffBind.deleteConfirm'),
        this.$t('attendanceAuditStaffBind.deleteTitle'), {
          type: 'warning'
        }).then(async () => {
        try {
          const res = await deleteAttendanceAuditStaff({ id: row.id })
          if (res && res.code === 0) {
            this.$message.success(this.$t('attendanceAuditStaffBind.deleteSuccess'))
            this.fetchData()
          } else {
            this.$message.error((res && res.msg) || this.$t('attendanceAuditStaffBind.deleteFailed'))
          }
        } catch (e) {
          console.error(e)
          this.$message.error(this.$t('attendanceAuditStaffBind.deleteFailed'))
        }
      }).catch(() => {})
    },
    handleGoBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.attendance-audit-staff-bind-container {
  padding: 20px;

  ::v-deep .el-card {
    border-radius: var(--ds-radius-md);
    border: 1px solid var(--ds-gray-300);
    box-shadow: var(--ds-shadow-sm);
  }

  ::v-deep .el-card__header {
    padding: var(--ds-spacing-md) var(--ds-spacing-lg);
    font-weight: 600;
    color: var(--ds-primary);
    background-color: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-300);
  }

  ::v-deep .el-pagination {
    margin-top: var(--ds-spacing-lg);
    text-align: right;
  }

  .flex {
    display: flex;
  }
  .justify-between {
    justify-content: space-between;
    align-items: center;
  }
}
</style>
